import { onUnmounted, ref, watch } from 'vue'
import type { Ref } from 'vue'

import { SfLog } from '@app/adapters/logger'
import graphFetch from '@app/application/report/composables/api/graph-fetch'
import type { GraphData } from '@app/application/report/screens/graphs-echarts/types'
import type { Locale } from '@app/shared/types/locale'

export interface UseGraphChartDataReturn {
  graphData: Ref<GraphData | null>
  loading: Ref<boolean>
  errored: Ref<boolean>
  fetchIfNeeded: () => Promise<void>
}

export function useGraphChartData(
  graphId: Ref<number | null>,
  reportTemplateId: Ref<number>,
  locale: Ref<Locale>,
  // Bumped when a graph is edited. The id does not change on an edit, so
  // without this the cached chart would keep rendering the old formula and
  // chart type until a page reload.
  revision?: Ref<number>
): UseGraphChartDataReturn {
  const graphData = ref<GraphData | null>(null)
  const loading = ref(false)
  const errored = ref(false)
  const fetchedForId = ref<number | null>(null)

  const alive = ref(true)
  onUnmounted(() => {
    alive.value = false
  })

  watch(graphId, (newId) => {
    if (newId !== fetchedForId.value) {
      graphData.value = null
      errored.value = false
      fetchedForId.value = null
    }
  })

  if (revision) {
    watch(revision, () => {
      const hadData = fetchedForId.value !== null
      fetchedForId.value = null
      errored.value = false
      if (hadData) void fetchIfNeeded()
    })
  }

  async function fetchIfNeeded(): Promise<void> {
    const id = graphId.value
    if (id === null) return
    if (fetchedForId.value === id) return

    const url =
      window.Silverfin.Routes.report_template_report_graph_path({
        report_template_id: reportTemplateId.value,
        id,
        company_id: parseInt(window.Silverfin.currentCompanyId, 10),
        firm_id: parseInt(window.Silverfin.currentFirmId, 10),
        _options: true,
      }) + `.json?language=${locale.value}`

    loading.value = true
    try {
      const data = await graphFetch<GraphData>(url)
      if (!alive.value || graphId.value !== id) return
      graphData.value = data
      fetchedForId.value = id
    } catch (error) {
      if (!alive.value || graphId.value !== id) return
      errored.value = true
      SfLog.error('Failed to load graph chart data', {
        error: error instanceof Error ? error : new Error(String(error)),
        extra: { graphId: id, reportTemplateId: reportTemplateId.value },
      })
    } finally {
      if (alive.value && graphId.value === id) {
        loading.value = false
      }
    }
  }

  return { graphData, loading, errored, fetchIfNeeded }
}
